import { useMemo } from 'react';

import { useTheme } from '@mui/material';
import { Line as RechartsLine, LineProps as RechartsLineProps } from 'recharts';

import { LineProps } from './types';
import { getThemeColor } from '../../../../utils/theme';
import { ANIMATION_DURATION_MS } from '../../_constants';
import { mapPaletteColorToDataColorToken } from '../../../../utils/mapPaletteColorToDataColorToken';
import { useChartConfig } from '../../ChartConfigProvider';
import { useChartSelection } from '../../ChartSelectionProvider';
import { ActiveDot } from '../_components/ActiveDot';
import { Dot } from '../_components/Dot';
import { useChartElementTooltip } from '../_hooks/useChartElementTooltip';
import { useGradient } from '../_hooks/useGradient';

export const Line = <T, V = string | number>({
  dataKey,
  isAnimationActive,
  getStrokeByPoint,
  strokeTransition = 'sharp',
  onDotClick,
  onActiveDotClick,
  dot = false,
  activeDot = true,
  testId = 'chart-line',
  stroke = 'indigo.500',
  strokeOpacity,
  strokeWidth = 1,
  tooltipConfig,
  selectionKey,
  ...rest
}: LineProps<T, V>) => {
  const selection = useChartSelection();
  const hiddenBySelection =
    selectionKey !== undefined
      ? selection !== undefined && !selection.isVisible(selectionKey)
      : false;
  const theme = useTheme();
  const semanticStroke = mapPaletteColorToDataColorToken(stroke, stroke);
  const themedStroke = getThemeColor(theme, semanticStroke);
  const getStopStylesByPoint = useMemo(() => {
    if (!getStrokeByPoint) {
      return;
    }
    return (dataPoint: T, index: number) => {
      const styles = getStrokeByPoint(dataPoint, index);
      return {
        stopColor: styles?.stroke
          ? getThemeColor(
              theme,
              mapPaletteColorToDataColorToken(styles.stroke, styles.stroke)
            )
          : themedStroke,
        stopOpacity: styles?.strokeOpacity ?? strokeOpacity,
        transition: styles?.strokeTransition,
      };
    };
  }, [getStrokeByPoint, theme, themedStroke, strokeOpacity]);
  const { gradientStops, gradientId } = useGradient(
    getStopStylesByPoint,
    strokeTransition
  );
  const hasCustomColoring = gradientStops.length > 0;
  const styledStroke = hasCustomColoring ? `url(#${gradientId})` : themedStroke;

  const internalId = useChartElementTooltip<T>({
    tooltipConfig,
    indicatorVariant: 'outlined',
    getStylesByPoint: getStopStylesByPoint,
  });

  const chartConfig = useChartConfig({ isAnimationActive });

  return (
    <>
      {hasCustomColoring && (
        <defs>
          <linearGradient id={gradientId} gradientUnits="userSpaceOnUse">
            {gradientStops}
          </linearGradient>
        </defs>
      )}
      <RechartsLine
        {...rest}
        hide={hiddenBySelection || (rest as { hide?: boolean }).hide}
        dataKey={dataKey as RechartsLineProps<T, V>['dataKey']}
        unit={internalId}
        dot={
          dot === true
            ? ({ index, key, ...rest }) => (
                <Dot
                  {...rest}
                  key={key}
                  index={index}
                  testId={`${testId}-dot-${index}`}
                  onClick={onDotClick}
                />
              )
            : dot
        }
        activeDot={
          activeDot === true
            ? ({ key, ...rest }) => (
                <ActiveDot
                  {...rest}
                  key={key}
                  onClick={onActiveDotClick}
                  testId={`${testId}-active-dot`}
                />
              )
            : activeDot
        }
        stroke={styledStroke}
        strokeOpacity={strokeOpacity}
        strokeWidth={strokeWidth}
        data-testid={testId}
        isAnimationActive={chartConfig.isAnimationActive}
        animationDuration={ANIMATION_DURATION_MS}
      />
    </>
  );
};
